// src/components/ConsultationChat.tsx
// Shared AI consultation chat: used standalone by ConsultationModule (queue-based
// ad-hoc consults) and embedded in VisitWorkflow's ConsultStage. The full
// transcript is persisted per-message to consultation_messages so a refresh or
// navigating away never loses the doctor's in-progress conversation.
//
// Phase 1 conversational generation (docs/HMS_V4_AI_CONTEXT_BUILDING_ASSESSMENT.md §5):
// when embedded in a visit, the note starts from today's captured data (no
// AI); replies stream in; the AI can leave fill-in fields and offer follow-up
// suggestions; the document is edited in place in DocumentEditor; and
// approval is blocked while any fill-in field is left.
//
// v3 conversational layout (docs/design/consultation/Conversation.dc.html,
// LiveStates.dc.html, docs/HMS_V4_CONSULTATION_REDESIGN_PLAN.md's v3 section,
// 2026-09-29) — embedded only: a two-column note canvas + always-visible
// AssistantColumn replaces the old collapsed "Conversation (n) ▸" transcript
// and the sticky-top progress bar. The standalone ConsultationModule path
// (no visitId) is untouched — see each render branch below.
import { useState, useEffect, useRef, useMemo, forwardRef, useImperativeHandle } from 'react';
import { loadConfig, getActiveModel } from '../aiConfig';
import { useResizableWidth } from '../hooks/useResizableWidth';
import { getConsultationMessages, addConsultationMessage, saveConsultation, getAuthUser, updateQueueStatus, getAiSettings, promoteConsultationToProfile, getConsultations, amendConsultation } from '../api';
import { TextPromptDialog } from './ui/TextPromptDialog';
import { AMENDABLE_DOC_TYPES, type AmendableNote } from '../features/consultation/NoteAmendment';
import { missingSections } from '../features/consultation/clinicalDraftSections';
import DocumentPreviewButtons from './DocumentPreviewButtons';
import { Button } from './ui/Button';
import { renderLiteMarkdown } from '../lib/markdownLite';
import { cleanAIDraft, splitDraftReply, stripTitleEcho, looksLikeDocument, MANUAL_MARKER } from '../lib/aiDraftCleanup';
// Legacy .jsx, imported as-is — allowJs resolves it with an inferred (not
// `any`) shape. Not converted here: it has no other consumer, so converting
// it isn't required to type this file, and doing it anyway would be scope
// creep beyond the structured-capture rebuild this pass is actually about.
import ConsultationVoicePanel from './ConsultationVoicePanel';
import VoiceMicButton from './VoiceMicButton';
import DocumentEditor, { type InsertableValue, type DocumentEditorHandle } from '../features/ai-assist/DocumentEditor';
import type { HeadingSection } from '../features/ai-assist/docAssist';
import { spliceSectionFromReply, detectSections, sectionBody, diffSections, type SectionChange } from '../features/ai-assist/documentSections';
import { generateDocument, type GenerationSuggestion, type AppliedMemoryItem } from '../features/ai-assist/streamGeneration';
import { startConsultationDraft, sourceMarkers, type Nudge, type SourceRef } from '../features/ai-assist/api';
import { useCompleteness } from '../features/ai-assist/useCompleteness';
import { NudgeList } from '../features/ai-assist/NudgeList';
import { SuggestionChips } from '../features/ai-assist/SuggestionChips';
import { PersonalisedChip } from '../features/ai-assist/PersonalisedChip';
import { MyAiPreferencesPanel } from '../features/ai-assist/MyAiPreferencesPanel';
import { createPreference, deletePreference, getNextProposal, type DoctorPreference } from '../features/ai-assist/memoryApi';
import { ProposalCard } from '../features/ai-assist/ProposalCard';
import { formatDocDate, removeAllPlaceholders, unfilledFieldLabels } from '../features/ai-assist/smartFields';
import { AssistantColumn } from '../features/ai-assist/AssistantColumn';
import { ChangeRibbon } from '../features/ai-assist/ChangeRibbon';
import type { ChatMessage } from '../features/ai-assist/ConsultationTranscript';
import { dispatchPlanHandoff } from '../features/consultation/planHandoff';
import { parseAiPrescriptionTable } from '../features/consultation/PrescriptionBuilder';
import { toast } from '../hooks/useToast';
import { INSERT_INTO_NOTE_EVENT } from '../features/clinical-references/api';
import { ToastAction } from './ui/Toast';

// Discharge Summary intentionally excluded: it needs real admission data
// (admission/discharge dates, ward, bed) this chat never has — it's used at
// the Consultation stage (or an ad-hoc queue consult), always before any IP
// admission exists. DischargeSummaryChat.tsx is the dedicated, properly
// admission-scoped equivalent, used from IPModule.tsx at actual ward discharge.
// OPD Prescription used to be a second document type generated here as free
// text, in parallel with the structured Prescription tab's medicine table —
// the same prescription entered twice, in two different shapes. It's been
// retired from this chat; the Prescription tab (PrescriptionBuilder, incl.
// its own "AI draft" action) is now the one place a prescription is written.
// This chat only ever produces Consultation Notes, so DocType is a single
// value rather than a doctor-facing choice.
const DOC_TYPES = ['Consultation Notes'] as const;
type DocType = typeof DOC_TYPES[number];

// The note↔assistant split (embedded, v3 layout) — width/drag mechanics
// live in useResizableWidth (shared with ConsultStage.tsx's rail).
const ASSISTANT_WIDTH_KEY = 'hms_consult_assistant_width';
const DEFAULT_ASSISTANT_WIDTH = 460;
const MIN_ASSISTANT_WIDTH = 340;
const MAX_ASSISTANT_WIDTH = 760;

/** The subset of a queue row (or ConsultStage's synthesized equivalent) this chat actually reads. */
export interface ConsultationQueueContext {
  id: number;
  patient_id?: number;
  uhid?: string;
  name: string;
  gender?: string;
  age?: number | null;
  bp_systolic?: number | null;
  bp_diastolic?: number | null;
  pulse_rate?: number | null;
  temperature?: number | null;
  spo2?: number | null;
  blood_group?: string | null;
  weight_kg?: number | null;
  height_cm?: number | null;
  bmi?: number | null;
  symptoms?: string | null;
  status: string;
}

export interface ConsultationApprovedPayload {
  doc_type: DocType;
  content: string;
  provider: string;
  model: string;
  consultation_id: number;
  document_id: number | null;
  document_uhid: string | null;
}

/**
 * How the document being approved was produced — sent as generation_source
 * (routes/consultation.js). Only 'ai' documents are measured as AI edits.
 *   template — started from today's captured data and completed by the doctor, no AI reply used
 *   ai       — an AI reply the doctor may have edited
 *   manual   — written from scratch in "Write manually"
 */
type DocSource = 'ai' | 'manual' | 'template';

// "Fill from clinical data": sent as the doctor's message, so it is revised
// against the document as it stands (see send()). The data itself reaches the
// model through extraContext and the vitals/patient objects.
const FILL_FROM_CLINICAL_DATA = 'Fill in this document from the clinical data already recorded for this visit: vitals, triage findings, allergies, diagnoses and patient history. Complete each field still to fill and each empty section only where that data supports it, and leave a field for me where it doesn\'t. Don\'t change anything I have already written.';

// The automatic first draft, sent silently (no visible "doctor asked for
// this" bubble — see send()'s `silent` option) when the hospital has AI
// enabled. D9: with AI on, the note starts as an AI draft over the captured
// data (via the hospital's active AI Assistant Role profile, applied
// server-side like any other generate call) instead of the deterministic
// no-AI skeleton startConsultationDraft() builds — that skeleton is used
// only when AI is off.
const DRAFT_FROM_CLINICAL_DATA = 'Draft the consultation notes from the clinical data already recorded for this visit: vitals, triage findings, allergies, diagnoses and patient history. Leave a fill-in field for anything the data doesn\'t support, and ask me directly if something clinically important seems missing or unclear.';

interface ConsultationChatProps {
  queueItem: ConsultationQueueContext;
  extraContext?: string;
  /**
   * Doctor-visible pre-consult triage summary (ailment/risk/pain, nurse-
   * reported allergies/meds, ailment-template findings, nurse notes) — only
   * actually rendered into the greeting when there's no `visitId` (i.e. no
   * ConsultStage rail alongside this chat to show it instead — see the
   * greeting effect below). Distinct from extraContext above, which feeds
   * the same underlying data to the AI silently, unconditionally, regardless
   * of what's shown on screen. Absent from ConsultationModule's standalone
   * fallback (no visit, so no pre-consult stage to summarize) — though that
   * path has no `visitId` either way, so the distinction is moot there.
   */
  preConsultSummary?: string;
  onApproved?: (payload: ConsultationApprovedPayload) => void;
  patientUhid?: string;
  onViewPatientRecord?: (uhid: string) => void;
  /**
   * Numeric visit id — present when embedded in ConsultStage (a tracked
   * visit), absent from ConsultationModule's standalone fallback (a queue
   * row with no tracked visit). Included in the saveConsultation payload so
   * the backend can backfill consultation_id onto this visit's structured
   * clinical rows (see routes/consultation.js's POST /save).
   */
  visitId?: number;
  /**
   * The visit's code (VIS-…), when embedded in a visit. Enables "start from
   * today's captured data" (GET /api/consultation/start/:visitCode) and the
   * completeness nudges. Absent on the standalone path, which starts empty.
   */
  visitCode?: string;
}

interface ApprovedDocRef { id: number; uhid: string }

/**
 * Readiness snapshot reported to an embedding stage (ConsultStage) so its own
 * commit bar can show "N fields to fill" / enable "Approve note" without
 * duplicating this component's internal state — see
 * docs/HMS_V4_CONSULTATION_REDESIGN_PLAN.md 1.3. Only meaningful when
 * `visitId` is set; the standalone ConsultationModule path has no outer bar
 * to report to and simply won't pass the callback.
 */
export interface ConsultationReadiness {
  hasDocument: boolean;
  approved: boolean;
  unfilledCount: number;
  unfilledLabels: string[];
  /** Whether "Record conversation" makes sense to show right now — mirrors
   * this component's own `!approved && !manualMode && docType` gate, so the
   * embedding stage's tab row can show/hide the same button without
   * duplicating that condition. */
  canRecord: boolean;
  /** Whether a recording is currently in progress, for the button's label. */
  recording: boolean;
  /** G4: headings of the hospital's six-section draft the note is missing (empty when the setting is off). */
  missingSections?: string[];
}

/** Imperative actions an embedding stage's own commit bar drives directly,
 * since the approve/unfilled-field controls moved out of this component's own
 * footer and into that bar (1.3). */
export interface ConsultationChatHandle {
  approve: () => void;
  removeUnfilled: () => void;
  focusNextField: () => void;
  /** Starts/stops voice recording — v3 moved the visible toggle itself into
   * the assistant column's composer footer (see AssistantColumn/
   * ConsultationComposer), so nothing outside this component calls this any
   * more, but it's kept for any other caller / the standalone header button. */
  toggleRecording: () => void;
}

interface ConsultationChatPropsWithReadiness extends ConsultationChatProps {
  onReadinessChange?: (readiness: ConsultationReadiness) => void;
}

const ConsultationChat = forwardRef<ConsultationChatHandle, ConsultationChatPropsWithReadiness>(function ConsultationChat({ queueItem, extraContext, preConsultSummary, onApproved, patientUhid, onViewPatientRecord, visitId, visitCode, onReadinessChange }, ref) {
  const [msgs, setMsgs]             = useState<ChatMessage[]>([]);
  const [msgsLoading, setMsgsLoading] = useState(true);
  const [inp, setInp]               = useState('Generate consultation notes based on available clinical findings and diagnosis');
  const [docType]                   = useState<DocType>('Consultation Notes');
  const [loading, setLoading]       = useState(false);
  // genDoc is the document as last produced (skeleton, AI reply or manual
  // draft) — sent as ai_draft. editDoc is what the doctor is editing and approves.
  const [genDoc, setGenDoc]         = useState<string | null>(null);
  const [editDoc, setEditDoc]       = useState('');
  // C6 — the ↻ "Regenerate this section" button is a ProseMirror decoration
  // widget keyed by position (docAssist.ts); ProseMirror reuses that DOM
  // node's original event listener across rebuilds whenever the key/position
  // are unchanged, even though buildDecorations recreates the JS closure
  // every time. A callback that isn't a stable setState setter (unlike
  // onDateRequest={setDateRequest}) would then read a STALE editDoc from
  // whichever render first mounted that particular button — confirmed live
  // (a manual edit made just before clicking regenerate was silently lost).
  // Reading through this ref instead means every version of the closure,
  // however old, sees the current document at call time.
  const editDocRef = useRef(editDoc);
  useEffect(() => { editDocRef.current = editDoc; }, [editDoc]);
  // C6 — the section key currently being regenerated, or null. Guards against
  // a second click firing a request while one is already in flight; it isn't
  // otherwise surfaced (the ↻ button stays clickable, it just no-ops).
  const [regeneratingKey, setRegeneratingKey] = useState<string | null>(null);
  const [approved, setApproved]     = useState(queueItem.status === 'Completed');
  const [saveError, setSaveError]   = useState('');
  const [aiCfg, setAiCfg]           = useState(loadConfig);
  const [showVoicePanel, setShowVoicePanel] = useState(false);
  const [approvedDoc, setApprovedDoc] = useState<ApprovedDocRef | null>(null); // lets us offer view/download right after approval
  const [approvedConsultationId, setApprovedConsultationId] = useState<number | null>(null); // for "Promote as hospital template" below
  const [promoteState, setPromoteState] = useState<'idle' | 'busy' | 'done'>('idle');
  const [promoteError, setPromoteError] = useState('');
  // Whether the last promote created a brand-new profile (no active
  // Consultation Notes profile existed yet) vs. proposed an edit to an
  // existing one — only changes the confirmation wording.
  const [promoteCreatedNew, setPromoteCreatedNew] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [manualText, setManualText] = useState('');
  const [docSource, setDocSource] = useState<DocSource>('ai');
  const [suggestions, setSuggestions] = useState<GenerationSuggestion[]>([]);
  // Phase 2 (Doctor AI Memory, §4.4): which of this doctor's preferences (if
  // any) shaped the current draft, and whether the "My AI preferences" panel
  // is open. Both are no-ops until the doctor has at least one active
  // preference — an empty applied_memory just means PersonalisedChip renders
  // nothing, matching the byte-identical-when-off guarantee.
  const [appliedMemory, setAppliedMemory] = useState<AppliedMemoryItem[]>([]);
  const [showPreferences, setShowPreferences] = useState(false);
  // Phase 3 (§4.4 point 1): the inline "you keep doing X — remember it?" card
  // shown once per session, right after approving a note. proposalCheckedRef
  // guards the one-per-session rule — even if approve() somehow ran twice,
  // the fetch only ever happens once.
  const [proposal, setProposal] = useState<DoctorPreference | null>(null);
  const proposalCheckedRef = useRef(false);
  const [streamingText, setStreamingText] = useState<string | null>(null);
  const [sources, setSources] = useState<SourceRef[]>([]);
  const [startContent, setStartContent] = useState('');
  // Set only when a vital was corrected after the note was drafted and the
  // note has no captured-data line to carry the correction into — see the
  // vitals re-sync effect below. Holds the corrected reading, for display.
  const [staleVitalsNotice, setStaleVitalsNotice] = useState('');
  // null while unknown (first render). Whether the hospital's AI switch is on
  // (ai_settings.ai_processing_enabled) — decides how the note starts (D9):
  // an AI draft over the captured data when on, the deterministic no-AI
  // skeleton when off. Fails closed (false) so an unreachable check never
  // fires an AI call the hospital didn't ask for.
  const [aiEnabled, setAiEnabled] = useState<boolean | null>(null);
  const chatScrollBoxRef = useRef<HTMLDivElement>(null);
  // Guards the silent auto-draft (below) against firing twice for the same
  // queue item — e.g. React 18 StrictMode's dev-only double-invoke of
  // effects, same concern VisitWorkflow.tsx's startedForRef documents. A real
  // AI call and a persisted chat message, unlike most of this effect's other
  // steps, so a duplicate here is visible and wasteful, not merely redundant.
  const autoDraftedForRef = useRef<number | null>(null);
  // Whether the note (once produced) is shown in full or as a short peek —
  // starts collapsed so a fresh reply's own chat message is what the
  // doctor's eye lands on, not a wall of document text. Standalone path only
  // (v3's embedded note canvas always fills the column — see below).
  const [noteExpanded, setNoteExpanded] = useState(embeddedOf(visitId));
  const documentEditorRef = useRef<DocumentEditorHandle>(null);

  // v3 conversational layout — embedded-only state (harmless, unread, on the
  // standalone path). preDocSnapshotRef/hasPriorDraftRef back the change
  // ribbon and its single-level Undo: the snapshot is whatever the document
  // stood at right before the turn/regenerate now in flight, and a ribbon
  // only ever appears from the SECOND draft onward (docs/design/consultation/
  // LiveStates.dc.html's "First open" states show no ribbon on the very
  // first fill). turnChanges is session-only and keyed by transcript message
  // index, for the per-turn "↧ <Section>" jump chips — there is no server-
  // side history to restore this from after a reload (see
  // ConsultationTranscript.tsx's own header comment).
  const preDocSnapshotRef = useRef<string | null>(null);
  const hasPriorDraftRef = useRef(false);
  const [changeSummary, setChangeSummary] = useState<SectionChange[] | null>(null);
  const [turnChanges, setTurnChanges] = useState<Map<number, SectionChange[]>>(new Map());
  const [latestChangeIndex, setLatestChangeIndex] = useState<number | null>(null);
  // "Writing · <Section>" in the assistant column's status line while a
  // reply streams — the last heading detected in the streamed-so-far text.
  const [writingSection, setWritingSection] = useState<{ key: string; heading: string } | null>(null);
  // Stop generation — an AbortController per in-flight request; aborting it
  // closes the fetch, which the backend already treats as a client
  // disconnect (routes/ai.js's res.on('close')) and aborts the provider call
  // server-side. Frontend-only wiring, nothing here changes on the backend.
  const abortRef = useRef<AbortController | null>(null);
  // The narrow-screen (<1280px) assistant dock's own expand/collapse state —
  // see AssistantColumn's `variant: 'dock'`.
  const [dockExpanded, setDockExpanded] = useState(false);
  // Assistant column ↔ note canvas split (embedded, ≥1280px only) — a
  // doctor-draggable divider rather than a fixed width, so the balance
  // between "reading the conversation" and "reading the note" is each
  // doctor's own call, not a layout decision made for them. Persisted both
  // locally and per-account (useResizableWidth) so it follows the doctor
  // across sessions and devices, not just this one browser.
  const { width: assistantWidth, startResize } = useResizableWidth({
    key: ASSISTANT_WIDTH_KEY, defaultValue: DEFAULT_ASSISTANT_WIDTH, min: MIN_ASSISTANT_WIDTH, max: MAX_ASSISTANT_WIDTH,
  });

  const modelId  = getActiveModel(aiCfg);
  // Embedded in ConsultStage (a tracked visit) vs. ConsultationModule's
  // standalone fallback — see each prop's own doc comment above. Drives
  // which of the two return branches below renders.
  const embedded = visitId != null;

  // Doctor AI Memory's one-time notice (D1, §4.4): shown once ever per
  // browser, not per session — a doctor who dismisses it shouldn't see it
  // again on every visit. Independent of whether this doctor has any
  // preferences yet; it's about the feature existing, not about a specific
  // applied preference (PersonalisedChip covers that).
  useEffect(() => {
    const KEY = 'hms_ai_memory_notice_shown';
    try {
      if (localStorage.getItem(KEY)) return;
      localStorage.setItem(KEY, '1');
    } catch { /* private browsing / storage blocked — show it every time rather than crash */ }
    toast({
      title: 'The assistant learns from your edits',
      description: 'Nothing is applied without your approval. Manage it any time in My AI preferences.',
      duration: 10000,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const refresh = () => setAiCfg(loadConfig());
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, []);

  useEffect(() => {
    getAiSettings().then((s) => { setAiEnabled(!!s.ai_processing_enabled); setStructuredDraft(!!s.structured_clinical_draft); }).catch(() => setAiEnabled(false));
  }, []);

  // Stamp "consultation started" the moment the doctor actually opens this
  // chat — the one hook point shared by both entry paths (the standalone
  // Queue view, which already flips status itself, and the Visit Workflow's
  // embedded Consultation stage, which otherwise never touches queue status
  // at all). Skipped once already approved, so re-opening a closed
  // consultation to review it can't revert its status backward.
  //
  // Only for a row that is genuinely waiting. Two statuses are deliberately
  // left alone: 'Completed' (re-opening a closed consultation to review it
  // must not revert it) and 'On Hold' — an interrupted consultation is
  // resumed by an explicit action (Resume consultation), never by the doctor
  // merely opening the screen, or the audit trail could not tell the two
  // apart and the doctor-availability check would be bypassed silently.
  //
  // The call can legitimately be refused (409 doctor_busy) if the doctor
  // picked someone else up in the meantime. Swallowed here as before: the
  // Queue screen and the Queue stage are where that refusal is raised and
  // explained, and by the time this mounts the server has normally already
  // granted the consultation.
  useEffect(() => {
    if (queueItem.status === 'Waiting') {
      updateQueueStatus(queueItem.id, 'In Consultation').catch(() => {});
    }
  }, [queueItem.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load persisted transcript for this queue item (resume after refresh/navigation).
  // Waits on aiEnabled (still null on first render) so the branch below knows
  // whether to seed the note with an AI draft or the no-AI skeleton before it
  // ever runs — re-running this whole effect once aiEnabled resolves is
  // harmless (getConsultationMessages() is a plain re-fetch) and far simpler
  // than splitting the decision across two effects racing each other.
  useEffect(() => {
    if (aiEnabled === null) return;
    setMsgsLoading(true);
    // The greeting is deterministic from queueItem's own fields, so it's synthesized
    // fresh on every load rather than persisted — avoids a duplicate-insert race
    // (e.g. two effect runs, or two tabs open on the same patient) from a
    // "persist only if empty" pattern.
    //
    // Vitals/Chief complaints/Pre-Consult text used to always print here too —
    // duplicating ConsultStage's rail cards, which show the same data as
    // icon tiles and chips right alongside this chat. `visitId` is only ever
    // passed by ConsultStage (never by ConsultationModule's standalone
    // fallback, the one path with no rail at all), so it doubles as the
    // signal for whether that duplication would actually happen — the
    // standalone path keeps the full text since it has nowhere else to show it.
    const vitalsBlock = embedded ? '' : `\n\n**Vitals on record:**\nBP: ${queueItem.bp_systolic||'—'}/${queueItem.bp_diastolic||'—'} mmHg · Pulse: ${queueItem.pulse_rate||'—'} bpm · Temp: ${queueItem.temperature||'—'}°F · SpO₂: ${queueItem.spo2||'—'}% · Wt: ${queueItem.weight_kg||'—'}kg${queueItem.bmi ? ` · BMI: ${queueItem.bmi}` : ''}`;
    const complaintsBlock = embedded ? '' : `\n\n**Chief complaints:** ${queueItem.symptoms || 'Not recorded'}`;
    const preConsultBlock = embedded ? '' : (preConsultSummary ? `\n\n${preConsultSummary}` : '');
    const nextStep = visitCode
      ? 'I\'ve started the note from today\'s captured data below — click anything to edit it, or describe your findings, diagnosis and plan and I\'ll complete it.'
      : 'Describe your clinical findings, diagnosis and treatment plan.';
    const greeting = `Hello Doctor! Patient **${queueItem.name}** (${queueItem.gender}, ${queueItem.age}y · UHID: ${queueItem.uhid}) is ready.${vitalsBlock}${complaintsBlock}${preConsultBlock}\n\n${nextStep}`;

    getConsultationMessages(queueItem.id).then((rows: ChatMessage[]) => {
      // Embedded: the doctor already sees identity/vitals/triage in
      // ConsultStage's banner and rail, and the note starts itself from
      // captured data below — a synthetic "hello, here's the patient again"
      // bubble added nothing but transcript noise. Standalone path is
      // unchanged: it has no rail to show any of this instead.
      setMsgs(embedded ? rows.map(r => ({ role: r.role, content: r.content })) : [{ role:'assistant', content: greeting }, ...rows.map(r => ({ role: r.role, content: r.content }))]);
      let restored = false;
      // Restore an unapproved draft so the approve bar reappears after a refresh
      if (queueItem.status !== 'Completed') {
        const lastDraft = [...rows].reverse().find(r => r.role === 'assistant' && (r.content.includes(MANUAL_MARKER) || looksLikeDocument(r.content)));
        if (lastDraft) {
          restored = true;
          hasPriorDraftRef.current = true;
          if (lastDraft.content.includes(MANUAL_MARKER)) {
            const cleaned = lastDraft.content.split('\n').filter(l => l.trim() !== MANUAL_MARKER).join('\n').trim();
            setGenDoc(cleaned); setEditDoc(cleaned); setDocSource('manual');
          } else {
            const cleaned = splitDraftReply(lastDraft.content).doc ?? cleanAIDraft(lastDraft.content);
            setGenDoc(cleaned); setEditDoc(cleaned); setDocSource('ai');
          }
        }
      }
      // Always fetch today's captured data — deterministic, no AI
      // (utils/capturedDataSkeleton.js). Its sources/content back the ⓘ
      // markers and Insert menu either way; whether it also SEEDS the note
      // depends on aiEnabled (D9): AI off keeps this exact skeleton as the
      // starting document, AI on instead asks the AI to draft over the same
      // clinical data (via the active AI Assistant Role profile), so the
      // skeleton's lines may not all reappear verbatim in that draft.
      if (visitCode && queueItem.status !== 'Completed') {
        // A restored draft is the doctor's own earlier work; a freshly seeded
        // one is not. This is what lets the vitals sync below know whether
        // replacing the note outright is safe.
        userEditedDocRef.current = restored;
        startConsultationDraft(visitCode).then((start) => {
          setSources(start.sources);
          setStartContent(start.content);
          if (!restored) {
            if (aiEnabled) {
              if (autoDraftedForRef.current !== queueItem.id) {
                autoDraftedForRef.current = queueItem.id;
                // Silent: no "doctor asked for this" bubble — the note simply
                // arrives already drafted, like opening a chat with a reply
                // waiting. Falls back to the skeleton if the call fails.
                send(DRAFT_FROM_CLINICAL_DATA, { silent: true, fallbackOnError: start.content });
              }
            } else {
              setGenDoc(start.content); setEditDoc(start.content); setDocSource('template');
            }
          }
        }).catch(() => { /* the doctor can still dictate or write the note */ });
      }
    }).catch(() => setMsgs([{ role:'assistant', content: greeting }]))
      .finally(() => setMsgsLoading(false));
  }, [queueItem.id, aiEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll only the standalone message list's own box to its latest message
  // (embedded's transcript lives inside AssistantColumn, which manages its
  // own scroll-to-bottom — see that file).
  useEffect(() => {
    if (embedded) return;
    const box = chatScrollBoxRef.current;
    if (box) box.scrollTop = box.scrollHeight;
  }, [msgs, streamingText, suggestions, embedded]);

  const persist = (role: ChatMessage['role'], content: string, extra: Record<string, unknown> = {}) =>
    addConsultationMessage({ queue_id: queueItem.id, role, content, doc_type: docType, ...extra }).catch(() => {});

  // silent: for the automatic first draft (see the load effect above) — the
  // instruction is a UI-internal trigger, not something the doctor typed, so
  // it's sent to the model but never shown or persisted as a chat bubble.
  // fallbackOnError: also only for that call — if the AI is enabled but this
  // particular attempt fails (provider outage, a momentary network error),
  // the doctor still needs something to start from rather than a blank chat;
  // falls back to the deterministic skeleton, same as the AI-off path.
  const send = async (text: string, { silent = false, fallbackOnError }: { silent?: boolean; fallbackOnError?: string } = {}) => {
    const typed = text.trim();
    if (!typed || !docType || loading) return;
    const userMsg: ChatMessage = { role: 'user', content: typed };
    if (!silent) { setMsgs([...msgs, userMsg]); setInp(''); }
    setLoading(true); setSuggestions([]); setStreamingText(''); setChangeSummary(null); setWritingSection(null);
    setAppliedMemory([]);
    // Only what the doctor actually typed is saved to the transcript.
    if (!silent) persist('user', typed);

    // With a document open, the AI revises it as it stands — so the doctor's
    // own edits and the values captured earlier in the visit survive, instead
    // of being regenerated from the conversation alone. The instruction is
    // deliberately CONDITIONAL ("if this asks you to change the note...
    // otherwise just answer") rather than an unconditional "revise it and
    // return the complete document" — that unconditional wording is what
    // used to make a genuine question ("what is O/E?") or an unrelated
    // remark get swallowed into "I've updated the draft — review it in the
    // note" every time, silently re-sending the unchanged note back instead
    // of actually answering. Letting the model judge per message (it's
    // reliably good at this) is more robust than a client-side heuristic
    // trying to guess "is this a question" from the wording alone.
    const current = editDoc.trim();
    const outgoing: ChatMessage[] = current
      ? [...msgs, {
          role: 'user',
          content: `${typed}\n\nCurrent document, for reference:\n\n${current}\n\nIf this message asks you to change the note, revise it and return the complete updated document, starting with the usual "AI-ASSISTED DRAFT" marker line — keep everything else exactly as written, including the doctor's own edits. If it's a question or a comment that doesn't call for a note change (e.g. asking what something means, or reporting a problem), just answer or respond directly instead — do not repeat, rewrite or otherwise touch the document, and do not include the marker.`,
        }]
      : [...msgs, userMsg];

    // Snapshot right before this turn touches anything, so the change ribbon
    // (embedded only — see ChangeRibbon usage below) and Undo have something
    // to revert to. Harmless, unread state on the standalone path.
    preDocSnapshotRef.current = editDoc;
    const newMsgIndex = msgs.length; // where this turn's assistant reply will land
    const controller = new AbortController();
    abortRef.current = controller;
    let wroteDocThisTurn = false;

    try {
      const result = await generateDocument({
        model: modelId,
        patient: queueItem as unknown as Record<string, unknown>,
        vitals: queueItem as unknown as Record<string, unknown>,
        messages: outgoing,
        doc_type: docType,
        temperature: aiCfg.temperature,
        max_tokens: aiCfg.maxTokens,
        extra_context: extraContext,
        // chat_reply: the AI talks to the doctor before the draft marker, and
        // that part is what the chat shows — the draft itself appears only in
        // the editor below, not twice. memory: Doctor AI Memory (Phase 2) —
        // the server only actually applies anything when this doctor has
        // active preferences for this doc_type; harmless to always declare.
        capabilities: ['smart_fields', 'suggestions', 'chat_reply', 'memory'],
      }, {
        signal: controller.signal,
        onDelta: (soFar) => {
          setStreamingText(soFar);
          // v3 — the reply streams straight into the note canvas itself
          // (embedded only; the standalone path's editor never sees a
          // partial reply, matching its own unchanged chat-bubble-only
          // streaming). The note is made read-only elsewhere below while
          // `loading` is true, so this never fights a concurrent edit.
          if (embedded) {
            const live = splitDraftReply(soFar);
            if (live.doc !== null) {
              setGenDoc(live.doc); setEditDoc(live.doc);
              wroteDocThisTurn = true;
              const liveSections = detectSections(live.doc);
              const last = liveSections[liveSections.length - 1];
              if (last) setWritingSection({ key: last.key, heading: last.heading });
            }
          }
        },
      });

      const reply = result.content;
      setMsgs(p => [...p, { role:'assistant', content: reply }]);
      persist('assistant', reply, { provider: aiCfg.provider });
      setSuggestions(result.suggestions);
      setAppliedMemory(result.applied_memory ?? []);
      const { doc } = splitDraftReply(reply);
      if (doc !== null || looksLikeDocument(reply)) {
        const cleaned = doc ?? cleanAIDraft(reply);
        setGenDoc(cleaned); setEditDoc(cleaned); setApproved(false); setDocSource('ai');
        // The change ribbon/per-turn chips only ever compare against a REAL
        // prior draft — not on the very first fill (docs/design/consultation/
        // LiveStates.dc.html's "First open" states show no ribbon there).
        if (hasPriorDraftRef.current && preDocSnapshotRef.current != null) {
          const changes = diffSections(preDocSnapshotRef.current, cleaned);
          if (changes.length) {
            setChangeSummary(changes);
            setTurnChanges(prev => new Map(prev).set(newMsgIndex, changes));
            setLatestChangeIndex(newMsgIndex);
          }
        }
        hasPriorDraftRef.current = true;
      }
    } catch(e) {
      const aborted = (e as { name?: string } | null)?.name === 'AbortError';
      if (aborted) {
        const note = '⏹ Stopped — the note reflects what was written so far.';
        setMsgs(p => [...p, { role:'assistant', content: note }]);
        persist('assistant', note);
        if (fallbackOnError != null && !wroteDocThisTurn) {
          setGenDoc(fallbackOnError); setEditDoc(fallbackOnError); setDocSource('template');
        } else if (wroteDocThisTurn) {
          hasPriorDraftRef.current = true;
        }
      } else {
        const errMsg = `⚠ Error: ${e instanceof Error ? e.message : String(e)}`;
        setMsgs(p => [...p, { role:'assistant', content: errMsg }]);
        persist('assistant', errMsg);
        if (fallbackOnError != null) {
          setGenDoc(fallbackOnError); setEditDoc(fallbackOnError); setDocSource('template');
        }
      }
    }
    abortRef.current = null;
    setStreamingText(null);
    setWritingSection(null);
    setLoading(false);
  };

  const sendMessage = () => send(inp);
  const stopGeneration = () => abortRef.current?.abort();

  // Phase 2 (§4.4 C8): the doctor tapped "💾 Always do this" on a suggestion
  // the AI offered after reading a lasting instruction in the chat. Saves
  // immediately as an active preference — no second approval, since the
  // doctor asked for it directly — with a brief Undo window.
  const handleRemember = async (suggestion: Extract<GenerationSuggestion, { type: 'remember' }>) => {
    setSuggestions((prev) => prev.filter((s) => s !== suggestion));
    try {
      const saved = await createPreference({ docType: docType, category: 'content', statement: suggestion.statement, origin: 'chat' });
      toast({
        variant: 'success', title: 'Remembered ✓', description: suggestion.statement, duration: 10000,
        action: <ToastAction altText="Undo" onClick={() => { deletePreference(saved.id).catch(() => {}); }}>Undo</ToastAction>,
      });
    } catch (e) {
      toast({ variant: 'danger', title: 'Could not save this preference', description: e instanceof Error ? e.message : undefined });
    }
  };

  // C6 (§5.2) — rewrites just the clicked section, leaving every other line
  // byte-for-byte as it was. A document-editor action, not a chat turn: no
  // bubble is added and the transcript is untouched, matching "other
  // sections untouched" being a silent, editor-scoped operation. Still
  // produces a change ribbon (embedded only) — always exactly one 'changed'
  // section, since spliceSectionFromReply only ever touches a section that
  // already existed.
  const regenerateSection = async ({ key, heading }: HeadingSection) => {
    if (!docType || regeneratingKey) return;
    setRegeneratingKey(key);
    try {
      const current = editDocRef.current;
      preDocSnapshotRef.current = current;
      const instruction = `Regenerate only the "${heading}" section below. Return the complete document with every other section — every other line, exactly as written, including the doctor's own edits — kept byte-for-byte identical. Do not rename, remove, reorder or add any section.\n\nCurrent document:\n\n${current}`;
      const result = await generateDocument({
        model: modelId,
        patient: queueItem as unknown as Record<string, unknown>,
        vitals: queueItem as unknown as Record<string, unknown>,
        messages: [{ role: 'user', content: instruction }],
        doc_type: docType,
        temperature: aiCfg.temperature,
        max_tokens: aiCfg.maxTokens,
        extra_context: extraContext,
        // No 'suggestions'/'chat_reply' — this isn't a conversation turn,
        // so there's no chat surface for either to land in.
        capabilities: ['smart_fields', 'memory'],
      });
      const { doc } = splitDraftReply(result.content);
      const cleaned = doc ?? cleanAIDraft(result.content);
      const spliced = spliceSectionFromReply(current, cleaned, key);
      if (spliced == null) {
        toast({ variant: 'danger', title: 'Could not regenerate that section', description: "The AI's reply didn't include a matching section — nothing changed." });
        return;
      }
      setGenDoc(spliced); setEditDoc(spliced);
      setChangeSummary([{ key, heading, kind: 'changed' }]);
      setLatestChangeIndex(null); // not a transcript turn — only the ribbon shows this one
      toast({ variant: 'success', title: `Regenerated: ${heading}` });
    } catch (e) {
      toast({ variant: 'danger', title: 'Could not regenerate that section', description: e instanceof Error ? e.message : undefined });
    } finally {
      setRegeneratingKey(null);
    }
  };

  const useManualDraft = () => {
    // A doctor typing "Consultation Notes" as their own first-line heading
    // out of habit would double up with the .docx's own title paragraph
    // exactly like an AI-echoed title would — same fix, narrower scope than
    // cleanAIDraft (no preamble/sign-off stripping of the doctor's own words).
    const content = stripTitleEcho(manualText.trim()).join('\n').trim();
    if (!content) return;
    setGenDoc(content); setEditDoc(content); setApproved(false); setDocSource('manual'); setManualMode(false);
    hasPriorDraftRef.current = true;
    const persisted = `${MANUAL_MARKER}\n\n${content}`;
    setMsgs(p => [...p, { role: 'assistant', content: persisted }]);
    persist('assistant', persisted);
  };

  // v3 — the change ribbon's Keep just dismisses it (the edit is already
  // live); Undo reverts to the snapshot taken right before the turn/
  // regenerate that produced it. Single-level: only the most recent change
  // can be undone this way — see this file's own plan for why.
  const keepChanges = () => setChangeSummary(null);
  const undoChanges = () => {
    if (preDocSnapshotRef.current != null) {
      setGenDoc(preDocSnapshotRef.current);
      setEditDoc(preDocSnapshotRef.current);
    }
    setChangeSummary(null);
    if (latestChangeIndex != null) {
      setTurnChanges(prev => { const next = new Map(prev); next.delete(latestChangeIndex); return next; });
      setLatestChangeIndex(null);
    }
  };
  const jumpToSection = (key: string) => { documentEditorRef.current?.scrollToSection(key); };

  // AI-off assistant column's plain dictation (embedded only) — appended
  // straight to the note, no AI round-trip (see AssistantColumn's own header
  // comment on why the chat composer doesn't show at all in this state).
  const dictateIntoNote = (text: string) => {
    setEditDoc((d) => (d.trim() ? `${d}\n${text}` : text));
    setGenDoc((d) => (d && d.trim() ? `${d}\n${text}` : text));
  };

  // 📚 References (G1) → "Insert into note": a chosen statement and its source
  // go at the end of the draft, for the doctor to edit like any other text.
  // An approved note is a filed record and is never changed here.
  useEffect(() => {
    const onInsert = (e: Event) => {
      const text = (e as CustomEvent<{ text?: string }>).detail?.text;
      if (!text) return;
      if (approved) {
        toast({ title: 'This note is already approved', description: 'Use Copy instead, or amend the note from Patient records.', variant: 'danger' });
        return;
      }
      setEditDoc((d) => (d.trim() ? `${d}\n${text}` : text));
      setGenDoc((d) => (d && d.trim() ? `${d}\n${text}` : text));
      toast({ title: 'Added to the end of the Consultation Notes draft', variant: 'success' });
    };
    window.addEventListener(INSERT_INTO_NOTE_EVENT, onInsert);
    return () => window.removeEventListener(INSERT_INTO_NOTE_EVENT, onInsert);
  }, [approved]);

  // v3 — "→ Move these to the prescription" (docs/design/consultation/
  // Conversation.dc.html marker 4): only offered when the Plan section's own
  // text actually parses as a medicine table, via the same parser
  // PrescriptionBuilder.tsx already falls back to for an AI-authored table.
  const planHandoffText = useMemo(() => {
    const section = detectSections(editDoc).find((s) => /plan/i.test(s.key));
    if (!section) return null;
    const body = sectionBody(editDoc, section).join('\n');
    return parseAiPrescriptionTable(body, 1).length > 0 ? body : null;
  }, [editDoc]);

  // A one-line status for the assistant column's header (embedded) / the
  // standalone path's own sticky bar — distinct wording per phase so
  // "I clicked send, is anything happening?" has a real answer. streamingText
  // is '' the instant a request goes out and only becomes non-empty once the
  // first chunk lands, hence the empty-string check rather than a truthy one.
  const processingLabel = !loading ? '' : (streamingText === '' || streamingText === null)
    ? 'Thinking…'
    : splitDraftReply(streamingText).doc === null ? 'Responding…'
    : writingSection ? `Writing · ${writingSection.heading}` : 'Writing the note…';
  const statusLabel = loading ? processingLabel : regeneratingKey ? 'Regenerating…' : '';

  // The doctor's eye lands on the note panel once a draft exists — the AI's
  // own latest chat message heads it. Only a genuine chat reply qualifies:
  // the text before the draft marker (chat_reply worked that turn) or a
  // short plain conversational turn. A message that IS the whole document
  // (looksLikeDocument, no marker found) has nothing separate worth
  // repeating.
  // Index 0 is the synthesized greeting only on the standalone path — embedded
  // dropped it entirely, so there's nothing to skip there.
  const greetingOffset = embedded ? 0 : 1;
  const latestAiNote = useMemo(() => {
    for (let i = msgs.length - 1; i >= greetingOffset; i--) {
      const m = msgs[i]!;
      if (m.role !== 'assistant' || m.content.includes(MANUAL_MARKER) || m.content.startsWith('✅') || m.content.startsWith('⚠ Error') || m.content.startsWith('⏹ Stopped')) continue;
      const { chat, doc } = splitDraftReply(m.content);
      if (doc !== null) return chat || null;
      return looksLikeDocument(m.content) ? null : m.content;
    }
    return null;
  }, [msgs, greetingOffset]);

  const unfilled = useMemo(() => unfilledFieldLabels(editDoc), [editDoc]);
  // G4: the hospital's six-section clinical draft, when switched on.
  const [structuredDraft, setStructuredDraft] = useState(false);
  const sectionsMissing = useMemo(
    () => (structuredDraft && docType === 'Consultation Notes' && editDoc.trim() ? missingSections(editDoc) : []),
    [structuredDraft, docType, editDoc]);
  // Deterministic nudges are for the no-AI path — with AI on, the doctor
  // reviews and refines the AI's own draft conversationally instead, so
  // skip both the fetch (visitCode undefined short-circuits the hook) and
  // the render below.
  const nudges: Nudge[] = useCompleteness(aiEnabled ? undefined : visitCode, genDoc && !approved ? editDoc : '');
  const markers = useMemo(() => sourceMarkers(sources), [sources]);

  // The Insert menu: today's date, a follow-up field, each line pre-filled
  // from captured data (so a line the doctor deleted can be put back), and
  // the doctor's name.
  const insertables = useMemo<InsertableValue[]>(() => {
    const items: InsertableValue[] = [
      { key: 'today', label: "Today's date", value: formatDocDate(new Date()) },
      { key: 'follow-up', label: 'Follow-up date (to fill in)', value: 'Follow-up: {{date|Follow-up date}}' },
    ];
    const lines = startContent.split('\n');
    for (const source of sources) {
      const line = lines.find(l => l.startsWith(`${source.label}:`));
      if (line) items.push({ key: `source-${source.label}`, label: source.label, value: line });
    }
    const doctorName = getAuthUser()?.full_name;
    if (doctorName) items.push({ key: 'doctor', label: 'Doctor name', value: doctorName });
    return items;
  }, [sources, startContent]);

  // ── Keeping the note in step with a corrected vital ────────────────────
  // The captured-data lines in the note are a SNAPSHOT, taken once by the
  // seeding effect above (keyed on queueItem.id — it does not re-run while
  // the doctor stays on this patient). Correcting a reading from the rail's
  // Vitals card (visits.js's /amend) refreshed the card immediately, because
  // the card renders straight off the visit's stage data, but the note kept
  // the value it was seeded with until a page reload. One screen then showed
  // two different BPs — and the stale one was the one about to be approved
  // and filed.
  //
  // Re-running the seeding effect is NOT the fix: it would discard whatever
  // the doctor has already typed into the note, and with AI on it would
  // spend a second draft call. This refetches the authoritative skeleton
  // (GET /api/consultation/start/:visitCode, which reads the vitals stage
  // data the amend just wrote) and replaces only the lines that came from
  // the Vitals stage, leaving every other line exactly as the doctor left it.
  const vitalsSignature = [
    queueItem.bp_systolic, queueItem.bp_diastolic, queueItem.pulse_rate, queueItem.temperature,
    queueItem.spo2, queueItem.weight_kg, queueItem.height_cm, queueItem.bmi, queueItem.symptoms,
  ].map(v => (v == null ? '' : String(v))).join('|');
  // editDoc as of the last render, readable from inside the async callback
  // below without making editDoc a dependency of the effect — which would
  // re-run it on every keystroke the doctor makes in the note. (Same ref the
  // section-regenerate flow above already keeps in step with editDoc — one
  // declaration serves both.)
  // Whether the doctor has typed into the note themselves. DocumentEditor
  // applies an outside value with { emitUpdate: false }, so its onChange
  // fires only for real user input — which makes this a reliable "is there
  // anything here worth protecting" flag, and lets the sync below replace an
  // untouched note outright instead of pattern-matching inside it.
  const userEditedDocRef = useRef(false);
  const docSourceRef = useRef(docSource);
  useEffect(() => { docSourceRef.current = docSource; }, [docSource]);
  const syncedVitalsRef = useRef<{ id: number; sig: string } | null>(null);

  useEffect(() => {
    // The seeding effect owns the FIRST snapshot for each patient; this one
    // only ever handles a change to it afterwards. Without this guard the
    // initial mount would fire a redundant refetch, and switching patients
    // would patch the incoming note while it was still being seeded.
    if (!syncedVitalsRef.current || syncedVitalsRef.current.id !== queueItem.id) {
      syncedVitalsRef.current = { id: queueItem.id, sig: vitalsSignature };
      setStaleVitalsNotice('');
      return;
    }
    if (syncedVitalsRef.current.sig === vitalsSignature) return;
    syncedVitalsRef.current = { id: queueItem.id, sig: vitalsSignature };
    // An approved note is a filed clinical record. A correction after that
    // point goes through the same amendment path every other signed document
    // in this app uses — never a silent rewrite of text already signed.
    if (!visitCode || approved) return;

    let cancelled = false;
    startConsultationDraft(visitCode).then((start) => {
      if (cancelled) return;
      // The ⓘ provenance markers and the Insert menu both read from these,
      // so both would otherwise keep offering the superseded reading.
      setSources(start.sources);
      setStartContent(start.content);

      const current = editDocRef.current;
      if (!current.trim()) { setStaleVitalsNotice(''); return; }

      // The simple case, and the common one: the note is still exactly the
      // captured-data skeleton this same endpoint produced, and the doctor
      // hasn't typed a word into it. There is nothing of theirs to preserve,
      // so take the fresh skeleton wholesale. No text matching happens at
      // all here, which means this path cannot half-work — the earlier
      // version of this code matched lines even in this case, and a single
      // mismatch left the note silently stale.
      if (!userEditedDocRef.current && docSourceRef.current === 'template') {
        setGenDoc(start.content);
        setEditDoc(start.content);
        setStaleVitalsNotice('');
        return;
      }

      // Otherwise there is doctor-written text in the document, so only the
      // lines the Vitals stage owns may be touched. Which labels those are
      // comes from the skeleton's own sources rather than a hardcoded list —
      // "Vitals" and, when the nurse recorded one, "Chief complaint".
      const vitalsLabels = start.sources
        .filter(s => s.source?.type === 'visit_stage' && s.source.stage === 'vitals')
        .map(s => s.label);
      // Leading "- ", "**" and whitespace are all things a round-trip
      // through the rich-text editor (or the doctor) can add in front of a
      // label, and none of them change which line this is.
      const labelOf = (line: string) => {
        const bare = line.replace(/^[\s>*_-]+/, '');
        return vitalsLabels.find(l => bare.toLowerCase().startsWith(`${l.toLowerCase()}:`)) || null;
      };
      const fresh = new Map<string, string>();
      for (const line of start.content.split('\n')) {
        const label = labelOf(line);
        if (label && !fresh.has(label)) fresh.set(label, line);
      }

      let replaced = 0;
      const applyTo = (doc: string) => doc.split('\n').map((line) => {
        const label = labelOf(line);
        const freshLine = label ? fresh.get(label) : undefined;
        if (freshLine) { replaced += 1; return freshLine; }
        return line;
      }).join('\n');

      const patched = applyTo(current);
      if (replaced > 0) {
        setEditDoc(patched);
        // genDoc is what gets sent as ai_draft on approval — leaving it on
        // the superseded text would file the old reading as "what the draft
        // said" even though the doctor approved the corrected one.
        setGenDoc(d => (d ? applyTo(d) : d));
      }
      // No line to carry the correction — an AI-drafted note can hold the
      // same readings as prose ("BP was 120/80 today"), and rewriting a
      // doctor's clinical prose by pattern-matching is exactly the kind of
      // silent edit this app doesn't make. Say so plainly instead, so the
      // stale reading can't be approved unnoticed. Deliberately NOT a silent
      // return: an unnoticed no-op here is the original bug.
      setStaleVitalsNotice(replaced === 0 && fresh.size > 0 ? [...fresh.values()].join(' · ') : '');
    }).catch(() => { /* the card is already correct; leave the note alone */ });

    return () => { cancelled = true; };
  }, [vitalsSignature, queueItem.id, visitCode, approved]);

  // Every edit the doctor makes to the note comes through here, so the sync
  // effect above knows there is now work of theirs in the document.
  const handleDocChange = (next: string) => {
    userEditedDocRef.current = true;
    setEditDoc(next);
  };

  // G5: Consultation Notes already approved for this visit are corrected by a
  // versioned amendment (with a reason), never by approving a second copy —
  // the server refuses that with 409 already_approved.
  const [amendTargetId, setAmendTargetId] = useState<number | null>(null);
  const [amendError, setAmendError] = useState<string | null>(null);
  const [amendSaving, setAmendSaving] = useState(false);

  const findApprovedNoteId = async (): Promise<number | null> => {
    if (approvedConsultationId) return approvedConsultationId;
    const rows = (queueItem.patient_id ? await getConsultations(queueItem.patient_id).catch(() => []) : []) as Array<AmendableNote & { queue_id?: number | null }>;
    const current = rows.filter(r => r.queue_id === queueItem.id && r.doc_type === docType && r.approved && !r.superseded_by_consultation_id);
    return current.length ? Math.max(...current.map(r => r.id)) : null;
  };

  const saveAmendment = async (reason: string) => {
    if (!amendTargetId) return;
    setAmendSaving(true); setAmendError(null);
    try {
      const saved = await amendConsultation(amendTargetId, editDoc, reason);
      setAmendTargetId(null);
      afterApproved(saved, editDoc, true);
    } catch (e) {
      setAmendError(e instanceof Error ? e.message : String(e));
    } finally { setAmendSaving(false); }
  };

  const approve = async () => {
    const content = editDoc;
    if (!content.trim() || !docType) return;
    if (unfilled.length) {
      setSaveError(`Fill in or remove ${unfilled.length === 1 ? 'this field' : 'these fields'} first: ${unfilled.join(', ')}`);
      return;
    }
    setSaveError('');
    if (AMENDABLE_DOC_TYPES.includes(docType)) {
      const existing = await findApprovedNoteId();
      if (existing) { setAmendError(null); setAmendTargetId(existing); return; }
    }
    try {
      const saved = await saveConsultation({
        patient_id: queueItem.patient_id,
        queue_id: queueItem.id,
        visit_id: visitId,
        doc_type: docType,
        ai_draft: genDoc,
        final_doc: content,
        // Only AI-generated documents are measured as AI edits — a
        // doctor-authored or template-started one compared against itself
        // would read as an AI draft accepted unchanged (routes/consultation.js).
        generation_source: docSource,
        approved: true,
        approved_by: getAuthUser()?.full_name || 'doctor',
      });
      afterApproved(saved, content, false);
    } catch(e) { setSaveError(e instanceof Error ? e.message : String(e)); }
  };

  const afterApproved = (saved: any, content: string, amended: boolean) => {
    {
      setApproved(true);
      setSuggestions([]);
      setChangeSummary(null);
      if (saved.document_id) setApprovedDoc({ id: saved.document_id, uhid: saved.document_uhid });
      setApprovedConsultationId(saved.id);
      setPromoteState('idle');
      setPromoteError('');
      // Provider/model deliberately not named here — a doctor-facing record
      // of clinical documentation shouldn't read as an ad for whichever AI
      // vendor the hospital happens to have configured this month, and it's
      // already in ai_call_log for anyone who genuinely needs it.
      const attribution = docSource === 'manual'
        ? 'Authored manually by doctor'
        : docSource === 'template'
          ? 'Started from captured visit data, completed by doctor'
          : 'AI-assisted draft, reviewed and approved by doctor';
      const note = amended
        ? `✅ **${docType}** amended (version ${saved.version}) and saved to ${queueItem.name}'s record. The earlier version is kept in the version history.`
        : `✅ **${docType}** approved and saved to ${queueItem.name}'s record.\nConsultation complete. _(${attribution})_`;
      setMsgs(p => [...p, { role:'assistant', content: note }]);
      persist('assistant', note);
      onApproved?.({ doc_type: docType, content, provider: aiCfg.provider, model: modelId, consultation_id: saved.id, document_id: saved.document_id, document_uhid: saved.document_uhid });

      // Phase 3: at most one proposal card per session — checked once, right
      // after the first successful approval.
      if (!proposalCheckedRef.current && docType) {
        proposalCheckedRef.current = true;
        getNextProposal(docType).then((r) => setProposal(r.proposal)).catch(() => {});
      }
    }
  };

  // "Promote as hospital template" — once a doctor is happy with a note
  // after refining it through the conversation, they can propose it as a
  // de-identified example on the hospital's active Consultation Notes AI
  // Assistant Role profile — or, if this hospital has no active Consultation
  // Notes profile yet, propose THIS note as a brand-new one. Goes to
  // Executive Director approval exactly like an Admin's own manual edit/
  // activation there (routes/ai-assistant-profiles.js) — this only stages
  // the proposal, it never takes effect on its own.
  const promoteToProfile = async () => {
    if (!approvedConsultationId) return;
    setPromoteState('busy');
    setPromoteError('');
    try {
      const result = await promoteConsultationToProfile(approvedConsultationId);
      setPromoteCreatedNew(!!result?.created);
      setPromoteState('done');
    } catch (e) {
      setPromoteState('idle');
      setPromoteError(e instanceof Error ? e.message : String(e));
    }
  };

  // 1.3 — the approve/unfilled-field controls live in ConsultStage's own
  // commit bar when embedded, driven through this ref rather than duplicating
  // this component's state in the parent.
  useImperativeHandle(ref, () => ({
    approve: () => { void approve(); },
    removeUnfilled: () => setEditDoc(d => removeAllPlaceholders(d)),
    focusNextField: () => { documentEditorRef.current?.focusNextField(); },
    toggleRecording: () => setShowVoicePanel(v => !v),
  }));

  // Reports readiness to the embedding stage whenever it changes — see
  // ConsultationReadiness's own doc comment above.
  useEffect(() => {
    onReadinessChange?.({
      hasDocument: genDoc != null, approved, unfilledCount: unfilled.length, unfilledLabels: unfilled, missingSections: sectionsMissing,
      canRecord: !approved && !manualMode && !!docType && !!aiEnabled, recording: showVoicePanel,
    });
  }, [genDoc, approved, unfilled, sectionsMissing, manualMode, docType, showVoicePanel, aiEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderMsg = (content: string) => renderLiteMarkdown(content);

  // ── Standalone (ConsultationModule) render — unchanged from before v3 ──
  if (!embedded) {
    // The message list + in-flight reply + suggestion chips, exactly as
    // before the v3 redesign — the standalone path keeps its own inline
    // transcript rather than AssistantColumn/ConsultationTranscript.
    const transcriptBody = (
      <>
        {msgsLoading && <div style={{ textAlign:'center', color:'#9CA3AF', fontSize:13, padding:20 }}>Loading conversation…</div>}
        {msgs.map((m, i) => {
          const isManual = m.role === 'assistant' && m.content.includes(MANUAL_MARKER);
          const split = m.role === 'assistant' && !isManual ? splitDraftReply(m.content) : { chat: m.content, doc: null };
          const cleaned = m.role === 'assistant' ? (split.doc ?? cleanAIDraft(m.content)) : '';
          const offerAsDocument = i >= greetingOffset && m.role === 'assistant' && !approved && !isManual && cleaned.length > 120
            && cleaned !== editDoc;
          const draftNote = split.doc !== null || isManual;
          return (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
              <div className={m.role === 'user' ? 'msg-bubble-user' : 'msg-bubble-ai'}>
                {m.role === 'user' ? m.content
                  : isManual ? <span style={{ fontSize: 12.5 }}>📝 You wrote the draft yourself — it's in the editor below.</span>
                  : split.doc !== null ? renderMsg(split.chat || 'I\'ve updated the draft below — review and edit it there.')
                  : renderMsg(m.content)}
                {draftNote && !isManual && (
                  <div style={{ marginTop: 6, fontSize: 11, color: '#6B7280' }}>📝 Draft updated — edit it below</div>
                )}
              </div>
              {offerAsDocument && (
                <button type="button"
                  onClick={() => { setGenDoc(cleaned); setEditDoc(cleaned); setApproved(false); setDocSource('ai'); }}
                  style={{ marginTop: 3, background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 11, color: 'var(--teal)', fontWeight: 600 }}>
                  ↺ Use {split.doc !== null ? 'this version of the draft' : 'this reply as the document'}
                </button>
              )}
            </div>
          );
        })}
        {loading && (
          streamingText
            ? (() => {
                const live = splitDraftReply(streamingText);
                return (
                  <div className="msg-bubble-ai">
                    {live.doc === null ? renderMsg(streamingText) : (
                      <>
                        {live.chat && renderMsg(live.chat)}
                        <div style={{ marginTop: live.chat ? 6 : 0, fontSize: 12, color: '#6B7280', animation: 'pulse 1s infinite' }}>✍ Writing the draft…</div>
                      </>
                    )}
                  </div>
                );
              })()
            : (
              <div className="msg-bubble-ai">
                <span style={{ animation: 'pulse 1s infinite', display: 'inline-block', fontSize: 12, color: '#6B7280' }}>
                  📝 Your document is being generated…
                </span>
              </div>
            )
        )}
        {!loading && !approved && suggestions.length > 0 && (
          <SuggestionChips suggestions={suggestions} onSend={send} onRemember={handleRemember} disabled={loading} />
        )}
        {!loading && appliedMemory.length > 0 && (
          <PersonalisedChip applied={appliedMemory} onOpenPreferences={() => setShowPreferences(true)} />
        )}
      </>
    );

    return (
      <div style={{ height: '100%' }}>
        <div className="card" style={{ display: 'flex', flexDirection: 'column', padding: 0, minHeight: 400, height: '100%' }}>
          <div style={{ padding: '10px 16px', borderBottom: '1px solid #F3F4F6', background: '#FAFAFA', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontWeight: 600, fontSize: 13 }}>
              {queueItem.name}
              <span style={{ color: '#6B7280', fontWeight: 400, fontSize: 12 }}> ({queueItem.gender}, {queueItem.age}y | {queueItem.uhid})</span>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap: 'wrap' }}>
                {!approved && !genDoc && docType && (
                  <button className="btn-ghost" style={{ padding:'6px 12px', fontSize:12, fontWeight:700 }} onClick={() => setManualMode(m => !m)}>
                    {manualMode ? '💬 Use AI chat instead' : '✍ Write manually'}
                  </button>
                )}
                {!approved && !manualMode && docType && aiEnabled && (
                  <button className="btn-primary" style={{ padding:'6px 12px', fontSize:12, fontWeight:700, boxShadow: showVoicePanel ? 'none' : '0 1px 3px rgba(0,0,0,.15)' }} onClick={() => setShowVoicePanel(v => !v)}>
                    {showVoicePanel ? '⏹ Stop recording' : '🎙 Record conversation'}
                  </button>
                )}
                {patientUhid && (
                  <button className="btn-ghost" style={{ padding: '6px 12px', fontSize: 12, fontWeight: 700, flexShrink: 0 }} onClick={() => onViewPatientRecord?.(patientUhid)}>
                    🗂 View patient record →
                  </button>
                )}
              </div>
            </div>
          </div>

          {loading && (
            <div style={{ position: 'sticky', top: 0, zIndex: 5, background: '#fff' }}>
              <div style={{ position: 'relative', height: 3, background: '#E5E7EB', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: 0, bottom: 0, width: '40%', background: 'linear-gradient(90deg, transparent, var(--teal, #0D9488), transparent)', animation: 'ai-progress-slide 1.1s ease-in-out infinite' }} />
              </div>
              <div style={{ padding: '4px 16px', fontSize: 11.5, fontWeight: 600, color: '#0F766E', background: '#F0FDFA' }}>
                🤖 {processingLabel}
              </div>
            </div>
          )}

          {showVoicePanel && !approved && !manualMode && (
            <ConsultationVoicePanel
              visitId={visitId}
              onClose={() => setShowVoicePanel(false)}
              onUseText={(text: string) => { setInp(text); setShowVoicePanel(false); }}
            />
          )}

          {manualMode && !approved ? (
            <div style={{ flex: 1, overflow: 'auto', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontSize: 12, color: '#6B7280' }}>Write the {docType} directly — no AI involved.</div>
              <DocumentEditor value={manualText} onChange={setManualText} insertables={insertables} ariaLabel={`${docType} (manual)`} />
              <button className="btn-primary" style={{ alignSelf: 'flex-end' }} disabled={!manualText.trim()} onClick={useManualDraft}>
                Use this as the document →
              </button>
            </div>
          ) : (
          <div ref={chatScrollBoxRef} style={{ flex: 1, overflow: 'auto', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {transcriptBody}
          </div>
          )}

          {genDoc && !approved && !manualMode && (
            <div style={{ padding: 12, borderTop: '1px solid #F3F4F6', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 10.5, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '.4px' }}>Consultation note</span>
                <button type="button" onClick={() => setNoteExpanded(v => !v)}
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 11.5, color: 'var(--teal)', fontWeight: 600 }}>
                  {noteExpanded ? '▲ Collapse' : '▼ Show full note'}
                </button>
              </div>
              <div style={{ maxHeight: noteExpanded ? 460 : 140, overflow: 'auto' }}>
                <DocumentEditor ref={documentEditorRef} value={editDoc} onChange={handleDocChange} insertables={insertables} sources={markers} onRegenerateSection={regenerateSection} readOnly={!!regeneratingKey} ariaLabel={docType} />
              </div>
              {staleVitalsNotice && (
                <div style={{ margin: '8px 0 0', padding: '8px 10px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 'var(--radius)', fontSize: 12, color: '#92400E', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <span style={{ flex: 1 }}>
                    ⚠ Vitals were corrected after this note was drafted — now {staleVitalsNotice}. This note doesn't carry that
                    correction; update it before approving.
                  </span>
                  <button type="button" onClick={() => setStaleVitalsNotice('')}
                    style={{ flexShrink: 0, background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 11.5, fontWeight: 600, color: '#92400E' }}>
                    Dismiss
                  </button>
                </div>
              )}
              {visitCode && !aiEnabled && <NudgeList nudges={nudges} />}
            </div>
          )}

          {saveError && <div style={{ padding: '8px 16px', background: '#FEE2E2', color: '#991B1B', fontSize: 12 }}>⚠ Could not save consultation: {saveError}</div>}

          {genDoc && !approved && (
            <div style={{ padding: '10px 16px', borderTop: '1px solid #FDE68A', background: '#FFFBEB', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: '#92400E', fontWeight: 600 }}>
                {docSource === 'ai' ? '⚡ AI document ready — awaiting doctor approval' : '📝 Document ready — awaiting doctor approval'}
              </span>
              {unfilled.length > 0 && (
                <span style={{ fontSize: 12, color: '#B45309' }}>
                  {unfilled.length} field{unfilled.length === 1 ? '' : 's'} to fill ·{' '}
                  <button type="button" onClick={() => setEditDoc(removeAllPlaceholders(editDoc))}
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12, color: 'var(--teal)', fontWeight: 600 }}>
                    Remove unfilled
                  </button>
                </span>
              )}
              {sectionsMissing.length > 0 && (
                <span style={{ fontSize: 12, color: '#B45309' }}>Sections missing: {sectionsMissing.join(', ')}</span>
              )}
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                <button className="btn-success" style={{ padding: '5px 14px', fontSize: 12 }} disabled={unfilled.length > 0 || !editDoc.trim()} onClick={approve}
                  title={unfilled.length ? `Fill in or remove: ${unfilled.join(', ')}` : undefined}>
                  ✅ Approve & save
                </button>
              </div>
            </div>
          )}
          {approved && (
            <div style={{ padding: '10px 16px', borderTop: '1px solid #6EE7B7', background: '#ECFDF5', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: '#065F46', fontWeight: 600 }}>✅ Document approved. Consultation complete.</span>
              {approvedDoc && (
                <div style={{ marginLeft: 'auto' }}>
                  <DocumentPreviewButtons uhid={approvedDoc.uhid} docId={approvedDoc.id} label={docType || 'Consultation document'} />
                </div>
              )}
            </div>
          )}
          {approved && proposal && (
            <div style={{ padding: '10px 16px', borderTop: '1px solid #F3F4F6' }}>
              <ProposalCard proposal={proposal} onDone={() => setProposal(null)} />
            </div>
          )}

          {!approved && !manualMode && (
            <div style={{ padding: 10, borderTop: '1px solid #F3F4F6' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <span style={{ fontSize: 11, color: '#6B7280' }}>Dictate instead of typing:</span>
                <VoiceMicButton onText={t => setInp(v => (v ? v + ' ' : '') + t)} />
                <button type="button" className="btn-ghost" style={{ marginLeft: 'auto', padding: '3px 10px', fontSize: 11.5, fontWeight: 600 }}
                  disabled={loading || !docType} onClick={() => send(FILL_FROM_CLINICAL_DATA)}
                  title="Let AI fill the note from this visit's vitals, triage, allergies, diagnoses and history">
                  ✨ Fill from clinical data
                </button>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <textarea style={{ resize: 'none', height: 52, flex: 1 }}
                  placeholder={`Describe findings, diagnosis and treatment for ${docType}…`}
                  value={inp} onChange={e => setInp(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }} />
                <button className="btn-primary" style={{ height: 52, minWidth: 64 }} onClick={sendMessage} disabled={loading}>
                  {loading ? '…' : '→'}
                </button>
              </div>
              <div style={{ fontSize: 11, color: '#6B7280', marginTop: 5 }}>Enter to send · Shift+Enter for new line</div>
            </div>
          )}
        </div>
        <TextPromptDialog
          open={amendTargetId != null}
          title="Amend the approved note?"
          description="These notes were already approved for this visit. Your changes are saved as a new version; the approved version stays in the record."
          label="Reason for the amendment"
          placeholder="e.g. Examination findings added after review"
          confirmLabel="Save amendment"
          multiline
          pending={amendSaving}
          error={amendError}
          onClose={() => setAmendTargetId(null)}
          onConfirm={saveAmendment}
        />
        <MyAiPreferencesPanel open={showPreferences} onOpenChange={setShowPreferences} />
      </div>
    );
  }

  // ── Embedded (ConsultStage) render — v3 conversational layout ──
  const assistantColumnCommonProps = {
    aiEnabled, approved, msgs, msgsLoading, loading, streamingText, statusLabel,
    changesByIndex: turnChanges, latestChangeIndex,
    onJumpToSection: jumpToSection, onUndoTurn: undoChanges,
    suggestions, onSendSuggestion: send, onRemember: handleRemember, appliedMemory,
    onOpenPreferences: () => setShowPreferences(true), proposal, onProposalDone: () => setProposal(null),
    onFillFromClinicalData: () => send(FILL_FROM_CLINICAL_DATA), nudges, onDictateIntoNote: dictateIntoNote,
    inp, onInpChange: setInp, onSend: sendMessage,
    onDictateIntoComposer: (t: string) => setInp(v => (v ? v + ' ' : '') + t),
    canRecord: !approved && !manualMode, recording: showVoicePanel, onToggleRecording: () => setShowVoicePanel(v => !v),
    latestLine: latestAiNote, onStop: stopGeneration,
  };

  return (
    <div className="flex h-full flex-col gap-2">
      {saveError && <div className="rounded-[var(--radius)] border border-danger/30 bg-danger-soft px-3 py-2 text-[12px] text-danger-ink">⚠ Could not save consultation: {saveError}</div>}
      {approved && (
        <div className="flex flex-wrap items-center gap-2.5 rounded-[var(--radius)] border border-success/40 bg-success-soft px-3 py-2">
          <span className="text-[12px] font-semibold text-success-ink">✅ Document approved. Consultation complete.</span>
          {approvedDoc && (
            <div className="ml-auto">
              <DocumentPreviewButtons uhid={approvedDoc.uhid} docId={approvedDoc.id} label={docType || 'Consultation document'} />
            </div>
          )}
        </div>
      )}
      {/* Only offered for a note that actually went through the AI
          conversation (not a purely manual or untouched-template one) —
          "happy with it after refinements" is specifically about that path.
          Never activates anything itself: it only stages a proposal an
          Executive Director still has to approve, same as an Admin's own
          manual edit to this profile (Admin Panel → AI Role). */}
      {approved && docSource === 'ai' && approvedConsultationId && (
        <div className="flex flex-wrap items-center gap-2.5 rounded-[var(--radius)] border border-line bg-card px-3 py-2 text-[12px]">
          {promoteState === 'done' ? (
            <span className="font-medium text-success-ink">
              {promoteCreatedNew
                ? '✅ Created and proposed as the new active "Consultation Notes" AI Assistant Role profile — an Executive Director will review it in Admin Panel → AI Role.'
                : '✅ Proposed — an Executive Director will review it in Admin Panel → AI Role.'}
            </span>
          ) : (
            <>
              <span className="text-ink-muted">Happy with this note after the refinements above?</span>
              <Button type="button" size="sm" variant="ghost" className="ml-auto" disabled={promoteState === 'busy'} onClick={promoteToProfile}>
                {promoteState === 'busy' ? 'Proposing…' : 'Promote as hospital template'}
              </Button>
            </>
          )}
          {promoteError && <span className="w-full text-danger-ink">⚠ {promoteError}</span>}
        </div>
      )}

      {/* min-h only, deliberately no flex-1 here: flex-1's flex-basis:0%
          would win over the explicit xl:h-[...] for this row's own sizing
          (both apply to the same, vertical, axis inside a flex-col parent),
          leaving the row — and everything inside it, including the
          assistant column's h-full — sized to its CONTENT instead of the
          viewport, which is what silently broke internal scrolling and
          pushed the composer far below the fold. The row doesn't need to
          grow; the explicit height (a vh calc, independent of any
          ancestor's own height) is the sizing mechanism. */}
      <div className="flex min-h-[520px] gap-0 xl:h-[calc(100vh-300px)]">
        {/* Assistant column — left, resizable (≥1280px only; the docked
            narrow-screen variant renders separately, below). Width lives on
            this wrapper, not on AssistantColumn itself, so the drag handle
            only has one box to resize. */}
        <div className="hidden shrink-0 pr-3 xl:block" style={{ width: assistantWidth }}>
          <AssistantColumn variant="column" dockExpanded={dockExpanded} onToggleDock={() => setDockExpanded(v => !v)} {...assistantColumnCommonProps} />
        </div>
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize the assistant column"
          onMouseDown={startResize}
          className="hidden w-3 shrink-0 cursor-col-resize items-center justify-center xl:flex"
        >
          <div className="h-10 w-1 rounded-full bg-border" />
        </div>
        {/* Note canvas */}
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-[var(--radius)] border border-line bg-card">
          {/* "Write manually" escape hatch — same "before any draft exists"
              gate the standalone path already used (once a draft exists,
              committing to the AI path or switching to manual would throw
              away work either way, so the toggle stops being offered). */}
          {!approved && !genDoc && docType && (
            <div className="flex shrink-0 justify-end border-b border-line px-3 py-1.5">
              <button type="button" className="text-[11.5px] font-semibold text-teal hover:underline" onClick={() => setManualMode(m => !m)}>
                {manualMode ? '💬 Use AI chat instead' : '✍ Write manually'}
              </button>
            </div>
          )}
          {showVoicePanel && !approved && !manualMode && (
            <ConsultationVoicePanel
              visitId={visitId}
              onClose={() => setShowVoicePanel(false)}
              onUseText={(text: string) => { setInp(v => (v ? v + ' ' : '') + text); setShowVoicePanel(false); }}
            />
          )}
          {manualMode && !approved ? (
            <div className="flex flex-1 min-h-0 flex-col gap-2.5 overflow-y-auto p-3.5">
              <div className="text-[12px] text-ink-muted">Write the {docType} directly — no AI involved.</div>
              <DocumentEditor value={manualText} onChange={setManualText} insertables={insertables} ariaLabel={`${docType} (manual)`} />
              <button type="button" className="btn-primary self-end" disabled={!manualText.trim()} onClick={useManualDraft}>
                Use this as the document →
              </button>
            </div>
          ) : genDoc && !approved ? (
            <>
              {changeSummary && <ChangeRibbon changes={changeSummary} onJump={jumpToSection} onKeep={keepChanges} onUndo={undoChanges} />}
              {staleVitalsNotice && (
                <div className="mx-2 mt-2 flex items-start gap-2 rounded-[var(--radius)] border border-warn bg-warn-soft px-2.5 py-2 text-[12px] text-caution-ink">
                  <span className="flex-1">
                    ⚠ Vitals were corrected after this note was drafted — now {staleVitalsNotice}. This note doesn't carry that
                    correction; update it before approving.
                  </span>
                  <button type="button" className="shrink-0 text-[11.5px] font-semibold hover:underline" onClick={() => setStaleVitalsNotice('')}>
                    Dismiss
                  </button>
                </div>
              )}
              <div className="min-h-0 flex-1 overflow-y-auto p-2">
                <DocumentEditor
                  ref={documentEditorRef} value={editDoc} onChange={handleDocChange} insertables={insertables} sources={markers}
                  onRegenerateSection={regenerateSection} readOnly={loading || !!regeneratingKey} ariaLabel={docType}
                />
              </div>
              {planHandoffText && (
                <div className="shrink-0 border-t border-line px-3 py-2">
                  <button
                    type="button"
                    onClick={() => { dispatchPlanHandoff(planHandoffText); }}
                    className="text-[11.5px] font-semibold text-teal hover:underline"
                  >
                    → Move these to the prescription
                  </button>
                </div>
              )}
            </>
          ) : approved ? (
            <div className="flex flex-1 items-center justify-center p-6 text-center text-[12.5px] text-ink-muted">
              ✅ This note was approved — see the summary and download link above.
            </div>
          ) : (
            <div className="flex flex-1 items-center justify-center p-6 text-[12.5px] text-ink-muted">
              {msgsLoading || (aiEnabled && loading) ? 'Preparing the note…' : 'The note will appear here.'}
            </div>
          )}
        </div>
      </div>
      {/* Docked narrow-screen (<1280px) variant — unrelated to the resizable
          split above, which is xl-only. */}
      <AssistantColumn variant="dock" dockExpanded={dockExpanded} onToggleDock={() => setDockExpanded(v => !v)} {...assistantColumnCommonProps} />

      <TextPromptDialog
        open={amendTargetId != null}
        title="Amend the approved note?"
        description="These notes were already approved for this visit. Your changes are saved as a new version; the approved version stays in the record."
        label="Reason for the amendment"
        placeholder="e.g. Examination findings added after review"
        confirmLabel="Save amendment"
        multiline
        pending={amendSaving}
        error={amendError}
        onClose={() => setAmendTargetId(null)}
        onConfirm={saveAmendment}
      />
      <MyAiPreferencesPanel open={showPreferences} onOpenChange={setShowPreferences} />
    </div>
  );
});

/** `useState`'s lazy initializer needs a plain function, not a hook-order-sensitive expression — visitId is only read once here, at mount, matching `embedded`'s own definition below it. */
function embeddedOf(visitId: number | undefined): boolean {
  return visitId != null;
}

export default ConsultationChat;
